/**
 * EditTemplateDialog Component
 * 
 * A modal dialog component for editing placement templates with:
 * - Download template section with Excel file download
 * - Drag & drop file upload interface
 * - Support for CSV, XLSX, XLS file formats
 * 
 * Props:
 * - isOpen: boolean - Controls modal visibility
 * - onClose: function - Called when modal should be closed
 */

import React, { Component } from 'react';
import { OmniIconComponent, AlertMessagesService, LoaderComponent } from '../../../../shared';
import { PlacementDetailsTableService } from '../../';
import '../../../../styles/editTemplateDialog.css';

export default class EditTemplateDialog extends Component {
    constructor(props) {
        super(props);
        this.fileInputRef = React.createRef();
        this.state = {
            isDragOver: false,
            selectedFile: null,
            spinnerCount: 0
        };
    }

    /**
     * Handle modal visibility when props change
     */
    componentDidUpdate(prevProps) {
        if (prevProps.isOpen !== this.props.isOpen) {
            const modalEl = document.getElementById('edit-template-modal');
            if (modalEl) {
                if (this.props.isOpen) {
                    modalEl.showModal();
                } else {
                    modalEl.close();
                    // Clear selected file when modal is closed
                    this.setState({ selectedFile: null, isDragOver: false });
                }
            }
        }
    }

    /**
     * Ensure modal is properly closed when component unmounts
     */
    componentWillUnmount() {
        const modalEl = document.getElementById('edit-template-modal');
        if (modalEl) {
            modalEl.close();
        }
    }

    /**
     * Handles downloading the placement template Excel file
     */
    handleDownloadTemplate = () => {
        AlertMessagesService.showInfoToastMessage('Preparing template download...');
        PlacementDetailsTableService.downloadPlacementTemplate()
            .then(response => {
                if(response.url){
                    const link = document.createElement('a');
                    link.href = response.url;
                    link.setAttribute('download', 'placement_template.xlsx');
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    AlertMessagesService.showSuccessToastMessage('Template downloaded successfully');
                } else if(response.job_id) {
                    this.checkJobStatusForExportPlacement(response.job_id);
                } else {
                    AlertMessagesService.showErrorToastMessage('No template found. Please upload a template first.');
                }
            })
            .catch(error => {
                AlertMessagesService.showErrorToastMessage('Failed to download template. Please try again.');
            });
    };

    /**
     * Checks the job status for exporting the placement template
     * and triggers the download if completed
     * @param {string} jobId - The ID of the export job
     */
    checkJobStatusForExportPlacement = (jobId) => {
        let payload = { jobId: jobId };
        PlacementDetailsTableService.checkExportJobStatus(payload).then(statusResponse => {
            if (statusResponse.status === "COMPLETED" && statusResponse.s3PresignedUrl) {
                // Create download link
                const link = document.createElement('a');
                link.setAttribute('href', statusResponse.s3PresignedUrl);
                link.setAttribute('download', 'placement_template.xlsx');
                link.style.visibility = 'hidden';

                // Trigger download
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                AlertMessagesService.showSuccessToastMessage('Template downloaded successfully');
            } else if (statusResponse.status === "IN PROGRESS" || statusResponse.status === "TRIGGERED") {
                // Retry after 3 seconds
                setTimeout(() => {
                    this.checkJobStatusForExportPlacement(jobId);
                }, 3000);
            } else {
                AlertMessagesService.showErrorToastMessage("Set up your server to enable file download. Contact your team administrator for assistance.");
            }
        }).catch(error => {
            AlertMessagesService.showErrorToastMessage("Failed to check job status. Please try again.");
        });
    }

    /**
     * Handles file selection and validation (doesn't upload yet)
     */
    handleFileSelection = (files) => {
        const file = files[0];
        if (!file) return;

        // Validate file type
        const allowedExtensions = ['csv', 'xlsx', 'xls'];
        const fileExtension = file.name.split('.').pop().toLowerCase();
        
        if (!allowedExtensions.includes(fileExtension)) {
            AlertMessagesService.showErrorToastMessage('Supported formats: CSV, XLSX, XLS');
            return;
        }

        // Store the selected file without uploading
        this.setState({ selectedFile: file });
        AlertMessagesService.showSuccessToastMessage('File selected. Click Upload to proceed.');
    };

    /**
     * Handles the actual file upload process
     */
    handleFileUpload = () => {
        const { selectedFile } = this.state;
        if (!selectedFile) return;

        // Convert file to base64
        const reader = new FileReader();
        reader.onload = (event) => {
            const base64String = event.target.result.split(',')[1]; // Remove data:mime;base64, prefix
            
            // Create payload with base64 file content
            const uploadPayload = {
                fileContent: base64String,
            };
            this.setState({ spinnerCount: 1 });    
            // Call parent's save handler (this will trigger the API call)
            PlacementDetailsTableService.uploadPlacementTemplate(uploadPayload)
                .then(response => {
                    // Clear selected file after upload
                    this.setState({ selectedFile: null });
                    this.handleClose();
                    AlertMessagesService.showSuccessToastMessage('Template uploaded successfully');
                })
                .catch(error => {
                    AlertMessagesService.showErrorToastMessage('Failed to upload template. Please try again.');
                }).finally(() => {
                    this.setState({ spinnerCount: 0 });
                });
        };
        
        reader.onerror = (error) => {
            AlertMessagesService.showErrorToastMessage('Failed to read file. Please try again.');
        };
        
        // Read file as data URL (base64)
        reader.readAsDataURL(selectedFile);
    };

    /**
     * Handles file input change
     */
    handleFileInputChange = (event) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            this.handleFileSelection(files);
        }
        // Clear the input
        event.target.value = '';
    };

    /**
     * Handles drag over event
     */
    handleDragOver = (event) => {
        event.preventDefault();
        this.setState({ isDragOver: true });
    };

    /**
     * Handles drag leave event
     */
    handleDragLeave = (event) => {
        event.preventDefault();
        this.setState({ isDragOver: false });
    };

    /**
     * Handles drop event
     */
    handleDrop = (event) => {
        event.preventDefault();
        this.setState({ isDragOver: false });
        
        const files = event.dataTransfer.files;
        if (files && files.length > 0) {
            this.handleFileSelection(files);
        }
    };

    /**
     * Handles clicking on the drag & drop area
     */
    handleDragAreaClick = () => {
        this.fileInputRef.current.click();
    };

    /**
     * Handles modal close
     */
    handleClose = () => {
        this.props.onClose();
    };

    /**
     * Handles action button click (upload selected file)
     */
    handleAction = () => {
        if (this.state.selectedFile) {
            this.handleFileUpload();
        }
    };

    /**
     * Removes the selected file
     */
    removeSelectedFile = () => {
        this.setState({ selectedFile: null });
    };

    render() {
        const { isDragOver, selectedFile } = this.state;

        return (
            <omni-modal id="edit-template-modal" class="edit-template-modal">
                <LoaderComponent spinnerCount={this.state.spinnerCount} displayWholePage={true}/>
                <span slot="header" className="modal-header">
                    Edit template
                </span>

                <div className="modal-body">
                    {/* Hidden file input */}
                    <input
                        ref={this.fileInputRef}
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        style={{ display: 'none' }}
                        onChange={this.handleFileInputChange}
                    />

                    {/* Download template section */}
                    <div className="download-template-section">
                        <h2 className="section-title">Download template</h2>

                        <div className="template-item">
                            <div className="template-info">
                                <OmniIconComponent
                                    icon-id="omni:informative:reports"
                                    className="template-icon"
                                />
                                <div className="template-details">
                                    <div className="template-name">Placement template.xlsx</div>
                                    <div className="template-description">Download the template to edit</div>
                                </div>
                            </div>
                            <button
                                onClick={this.handleDownloadTemplate}
                            >
                                <OmniIconComponent
                                    icon-id="omni:interactive:download"
                                    className="download-icon"
                                />
                                Download
                            </button>
                        </div>
                    </div>

                    {/* Upload section */}
                    <div className="upload-section">
                        <div
                            className={`drag-drop-area ${isDragOver ? 'drag-over' : ''} ${selectedFile ? 'file-selected' : ''}`}
                            onDragOver={this.handleDragOver}
                            onDragLeave={this.handleDragLeave}
                            onDrop={this.handleDrop}
                            onClick={!selectedFile ? this.handleDragAreaClick : undefined}
                        >
                            {selectedFile ? (
                                <div className="selected-file-display">
                                    <div className="file-info">
                                        <OmniIconComponent
                                            icon-id="omni:informative:reports"
                                            className="selected-file-icon"
                                        />
                                        <div className="file-details">
                                            <div className="file-name">{selectedFile.name}</div>
                                            <div className="file-size">
                                                {(selectedFile.size / 1024).toFixed(1)} KB
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        className="icon medium"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            this.removeSelectedFile();
                                        }}
                                    >
                                        <OmniIconComponent
                                            icon-id="omni:interactive:close"
                                            className="remove-icon"
                                        />
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <OmniIconComponent
                                        icon-id="omni:interactive:upload"
                                        className="upload-icon"
                                    />
                                    <div className="upload-text">
                                        <strong>Drag & drop or select file to upload</strong>
                                    </div>
                                    <div className="supported-formats">
                                        Supported formats: CSV, XLSX, XLS
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <div slot="footer" className="modal-footer">
                    <button className="tertiary" onClick={this.handleClose}>
                        Cancel
                    </button>
                    <button onClick={this.handleAction} disabled={!selectedFile}>
                        Upload
                    </button>
                </div>
            </omni-modal>
        );
    }
}

